"use client";

import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import LottieLoader from "@/components/LottieLoader";
import CoursesGridSkeleton from "@/components/skeletons/CoursesGridSkeleton";
import { ArrowRight, BookOpen } from "lucide-react";
import CourseCard from "./CourseCard";
import EmptyState from "./EmptyState";
import Section from "./Section";
import SectionHeading from "./SectionHeading";
import { useCourses } from "../hooks/useCourses";
import { getRegionLocaleHref, getClientRegion } from "@/services/region";
import type {
  LearningCoursesQuery,
  LearningCoursesResponse,
  LearningSectionItem,
} from "../types/learning";
import { resolveLocalizedValue } from "../utils/localization";

type CoursesProps = {
  title?: string;
  categorySlug?: string;
  initialVisible?: number;
  fetchCourses?: (
    locale: string,
    query?: LearningCoursesQuery,
  ) => Promise<LearningCoursesResponse>;
  categoryOptions?: LearningSectionItem[];
  showCategoryFilter?: boolean;
  hideWhenEmpty?: boolean;
};

export default function Courses({
  title = "Courses",
  categorySlug,
  initialVisible = 6,
  fetchCourses,
  categoryOptions = [],
  showCategoryFilter = false,
  hideWhenEmpty = false,
}: CoursesProps) {
  const locale = useLocale();
  const t = useTranslations("web.courses");
  const pathname = usePathname();
  const [selectedCategorySlug, setSelectedCategorySlug] = useState(
    categorySlug ?? "",
  );
  const regionSlug = getClientRegion(pathname) || undefined;
  const { courses, loading, loadingMore, hasMore, loadMore } = useCourses(
    locale,
    {
      categorySlug: selectedCategorySlug || undefined,
      perPage: initialVisible,
      regionSlug,
    },
    fetchCourses,
  );
  const resolvedTitle = title === "Courses" ? t("courses_heading") : title;

  if (loading) {
    return (
      <CoursesGridSkeleton
        cards={initialVisible}
        showCategoryFilter={showCategoryFilter && categoryOptions.length > 0}
      />
    );
  }

  if (hideWhenEmpty && courses.length === 0) {
    return null;
  }

  return (
    <Section>
      <SectionHeading>{resolvedTitle}</SectionHeading>

      {showCategoryFilter && categoryOptions.length > 0 ? (
        // Filter chips: pill-shaped with a visible resting border, so an
        // unselected chip reads as a control rather than as plain text on
        // white, and the selected one is unambiguous.
        <div className="mb-8 flex flex-wrap gap-2.5">
          <button
            className={`cursor-pointer rounded-[10px] border px-4 py-2 text-sm font-semibold transition ${
              !selectedCategorySlug
                ? "border-primary bg-primary text-white"
                : "border-slate-200 bg-white text-primary hover:border-secondary hover:text-secondary"
            }`}
            onClick={() => setSelectedCategorySlug("")}
            type="button"
          >
            {t("categories_heading")}
          </button>
          {categoryOptions.map((category) => {
            const categoryName = resolveLocalizedValue(category.name, locale);

            return (
              <button
                key={category.id}
                className={`cursor-pointer rounded-[10px] border px-4 py-2 text-sm font-semibold transition ${
                  selectedCategorySlug === category.slug
                    ? "border-primary bg-primary text-white"
                    : "border-slate-200 bg-white text-primary hover:border-secondary hover:text-secondary"
                }`}
                onClick={() => setSelectedCategorySlug(category.slug)}
                type="button"
              >
                {categoryName}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="grid grid-cols-12 gap-6">
        {courses.map((course) => {
          const imageSrc = course.image ?? course.category?.image;
          const categoryName = resolveLocalizedValue(
            course.category?.name,
            locale,
            "General",
          );

          return (
            <div
              key={course.id}
              className="col-span-12 md:col-span-6 lg:col-span-4"
            >
              <CourseCard
                href={getRegionLocaleHref(
                  pathname,
                  locale,
                  `/courses/${course.slug}`,
                )}
                image={imageSrc}
                imageAlt={course.name}
                imageWrapperClassName=" bg-course-cover overflow-hidden"
                imageClassName="transition-transform duration-500 group-hover/card:scale-105"
                placeholder={
                  <span className="line-clamp-2 px-4 text-center text-xl font-bold text-on-course-cover/95">
                    {course.name}
                  </span>
                }
                // Softer radius, a real resting border instead of shadow-only,
                // and a lift on hover. `group/card` drives the image zoom.
                className="group/card h-full overflow-hidden rounded-2xl border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:border-secondary/40 hover:shadow-lg"
                contentClassName="p-5"
                // Category first and set as a small uppercase label, so the
                // course name is unambiguously the primary line - previously
                // the 16px category competed with the 20px title.
                title={course.name}
                titleClassName="!text-[20px] !font-bold !leading-snug !text-primary group-hover/card:!text-secondary transition-colors"
                category={categoryName}
                categoryClassName="!text-[11px] !font-bold !uppercase !tracking-[0.08em] !text-secondary"
                footer={
                  <div className="w-full border-t border-slate-100 pt-4">
                    {/* A styled span rather than a <button>: the whole card is
                        already a <Link>, and an interactive element nested in
                        an anchor is invalid markup. The solid fill replaces the
                        old plain-text label, which read as a caption rather
                        than a call to action. */}
                    <span className="flex w-full items-center justify-center gap-2 rounded-[10px] bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-all duration-300 group-hover/card:bg-secondary group-hover/card:shadow-md">
                      {t("enroll_now")}
                      <ArrowRight
                        aria-hidden="true"
                        size={16}
                        className="transition-transform duration-300 group-hover/card:translate-x-1 rtl:rotate-180 rtl:group-hover/card:-translate-x-1"
                      />
                    </span>
                  </div>
                }
              />
            </div>
          );
        })}
      </div>

      {courses.length === 0 ? (
        <EmptyState
          icon={<BookOpen size={28} />}
          title={t("no_courses")}
          description={t("no_courses_hint")}
        />
      ) : null}

      {hasMore && (
        <div className="mt-12 flex justify-center">
          <button
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="inline-flex min-w-[200px] cursor-pointer items-center justify-center rounded-[10px] border-2 border-primary px-8 py-3 text-base font-semibold text-primary transition-colors duration-200 hover:bg-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loadingMore ? (
              <LottieLoader size={18} label={t("loading")} />
            ) : (
              t("load_more")
            )}
          </button>
        </div>
      )}
    </Section>
  );
}
